/**
 * InteractivePieChart - Интерактивная круговая диаграмма расходов по категориям
 * Как в мобильном приложении
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icon';
import { formatCurrency } from '../../utils/format';
import './InteractivePieChart.css';

interface CategoryExpense {
  category: string;
  amount: number;
  percentage: number;
}

interface InteractivePieChartProps {
  expenses: CategoryExpense[];
  currency: string;
  isLoading?: boolean;
  grandTotal?: number;
  onCategoryPress?: (category: string) => void;
}

const PIE_COLORS = [
  '#1C78FF', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
];

function getCategoryColor(index: number): string {
  return PIE_COLORS[index % PIE_COLORS.length];
}

function stripEmojis(text: string): string {
  return text.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu, '').trim();
}

export function InteractivePieChart({
  expenses,
  currency,
  isLoading = false,
  grandTotal,
  onCategoryPress,
}: InteractivePieChartProps) {
  const { t } = useTranslation();
  const [selectedSlice, setSelectedSlice] = useState<string | null>(null);

  const total = grandTotal ?? expenses.reduce((sum, e) => sum + e.amount, 0);

  const displayExpenses = useMemo(() => {
    if (expenses.length === 0) return [];
    return expenses
      .map(e => ({
        ...e,
        percentage: total > 0 ? (e.amount / total) * 100 : 0,
      }))
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 8);
  }, [expenses, total]);

  if (isLoading) {
    return (
      <div className="interactive-pie-chart interactive-pie-chart--loading">
        <div className="interactive-pie-chart__spinner">
          <Icon name="loader" size={32} />
        </div>
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  if (displayExpenses.length === 0) {
    return (
      <div className="interactive-pie-chart interactive-pie-chart--empty">
        <Icon name="pie-chart" size={48} color="#9CA3AF" />
        <span>{t('analytics.noExpensesData') || 'Нет данных о расходах'}</span>
      </div>
    );
  }

  return (
    <div className="interactive-pie-chart">
      <div className="interactive-pie-chart__content">
        <div className="interactive-pie-chart__chart-container">
          <svg className="interactive-pie-chart__svg" viewBox="0 0 200 200">
            <defs>
              {displayExpenses.map((_, index) => (
                <radialGradient 
                  key={`gradient-${index}`} 
                  id={`home-pie-gradient-${index}`}
                  cx="40%" 
                  cy="40%"
                >
                  <stop offset="0%" stopColor={getCategoryColor(index)} stopOpacity="1" />
                  <stop offset="100%" stopColor={getCategoryColor(index)} stopOpacity="0.85" />
                </radialGradient>
              ))}
            </defs>
            {(() => {
              let currentAngle = -90;
              return displayExpenses.map((cat, index) => {
                const percentage = cat.percentage || 0;
                const angle = (percentage / 100) * 360;
                const startAngle = currentAngle;
                const endAngle = currentAngle + angle;
                currentAngle = endAngle;

                const midAngle = (startAngle + endAngle) / 2;
                const midRad = (midAngle * Math.PI) / 180;
                const isSelected = selectedSlice === cat.category;

                const startRad = (startAngle * Math.PI) / 180;
                const endRad = (endAngle * Math.PI) / 180;

                const x1 = 100 + 70 * Math.cos(startRad);
                const y1 = 100 + 70 * Math.sin(startRad);
                const x2 = 100 + 70 * Math.cos(endRad);
                const y2 = 100 + 70 * Math.sin(endRad);

                const largeArcFlag = angle > 180 ? 1 : 0;

                const pathData = [
                  `M 100 100`,
                  `L ${x1} ${y1}`,
                  `A 70 70 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                  'Z'
                ].join(' ');

                const offsetDistance = isSelected ? 8 : 0;
                const offsetX = offsetDistance * Math.cos(midRad);
                const offsetY = offsetDistance * Math.sin(midRad);
                const scale = isSelected ? 1.05 : 1;

                return (
                  <g 
                    key={`slice-${index}`}
                    className="interactive-pie-chart__slice-group"
                    style={{ 
                      transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
                      transformOrigin: '100px 100px',
                    }}
                    onClick={() => {
                      setSelectedSlice(isSelected ? null : cat.category);
                      if (onCategoryPress && !isSelected) {
                        onCategoryPress(cat.category);
                      }
                    }}
                  >
                    <path
                      d={pathData}
                      fill={`url(#home-pie-gradient-${index})`}
                      className={`interactive-pie-chart__slice ${isSelected ? 'interactive-pie-chart__slice--selected' : ''}`}
                    />
                  </g>
                );
              });
            })()}
            {/* Центральный круг (donut) */}
            <circle cx="100" cy="100" r="45" fill="var(--color-background)" />
          </svg>
          
          {/* Центр с общей суммой */}
          <div className="interactive-pie-chart__center">
            <span className="interactive-pie-chart__center-label">{t('home.expenses')}</span>
            <span className="interactive-pie-chart__center-amount">
              {formatCurrency(total, currency)}
            </span>
          </div>
        </div>

        {/* Легенда */}
        <div className="interactive-pie-chart__legend">
          {displayExpenses.map((cat, index) => (
            <div 
              key={index}
              className={`interactive-pie-chart__legend-item ${selectedSlice === cat.category ? 'interactive-pie-chart__legend-item--selected' : ''}`}
              onClick={() => {
                setSelectedSlice(selectedSlice === cat.category ? null : cat.category);
                if (onCategoryPress) {
                  onCategoryPress(cat.category);
                }
              }}
            >
              <div 
                className="interactive-pie-chart__legend-color"
                style={{ backgroundColor: getCategoryColor(index) }}
              />
              <div className="interactive-pie-chart__legend-info">
                <span className="interactive-pie-chart__legend-name">
                  {stripEmojis(cat.category)}
                </span>
                <span className="interactive-pie-chart__legend-value">
                  {formatCurrency(cat.amount, currency)} ({cat.percentage?.toFixed(1)}%)
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default InteractivePieChart;
